import React, { useState, useEffect } from 'react';
import { Client } from '../types';
import { clientAPI } from '../services/api';

interface ClientListProps {
  onSelectClient: (client: Client) => void;
  onAddClient: () => void;
}

const ClientList: React.FC<ClientListProps> = ({ onSelectClient, onAddClient }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    try { const response = await clientAPI.getAll(); setClients(response.data); }
    catch (error) { console.error('Error fetching clients:', error); }
    finally { setLoading(false); }
  };

  const filteredClients = clients.filter(client => {
    const s = searchTerm.toLowerCase();
    return (
      client.name.toLowerCase().includes(s) ||
      client.company_name?.toLowerCase().includes(s) ||
      client.email.toLowerCase().includes(s) ||
      client.phone_cell.toLowerCase().includes(s) ||
      client.phone_work?.toLowerCase().includes(s) ||
      client.imei_number?.toLowerCase().includes(s) ||
      client.iccid?.toLowerCase().includes(s) ||
      client.serial_number?.toLowerCase().includes(s) ||
      (client.devices && client.devices.some(d =>
        d.vehicle_id?.toLowerCase().includes(s) ||
        d.imei_number.toLowerCase().includes(s) ||
        d.serial_number.toLowerCase().includes(s) ||
        d.iccid.toLowerCase().includes(s)
      ))
    );
  });

  const getLoginBadge = (lastLogin?: string) => {
    if (!lastLogin) return { text: 'Never', bg: '#fee2e2', color: '#991b1b' };
    const days = Math.floor((Date.now() - new Date(lastLogin).getTime()) / 86400000);
    if (days <= 7) return { text: `${days}d ago`, bg: '#dcfce7', color: '#166534' };
    if (days <= 30) return { text: `${days}d ago`, bg: '#fef3c7', color: '#92400e' };
    return { text: `${days}d ago`, bg: '#fee2e2', color: '#991b1b' };
  };

  const totalDevices = clients.reduce((sum, c) => sum + (c.devices?.length || 0), 0);
  const totalRevenue = clients.reduce((sum, c) => sum + (c.monthly_fee || 0), 0);

  if (loading) return <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>Loading clients...</div>;

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '26px', fontWeight: 700, margin: 0, background: 'linear-gradient(135deg, #2563eb, #7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>👤 Client Management</h1>
          <p style={{ color: '#888', margin: '4px 0 0 0', fontSize: '14px' }}>Manage your MyAsset clients and their devices</p>
        </div>
        <button onClick={onAddClient} style={{ padding: '12px 24px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #2563eb, #7c3aed)', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '14px', boxShadow: '0 2px 8px rgba(37,99,235,0.3)' }}>
          + Add Client
        </button>
      </div>

      {/* KPI tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: 'Total Clients', value: clients.length, icon: '👤', color: '#2563eb' },
          { label: 'Total Devices', value: totalDevices, icon: '📡', color: '#7c3aed' },
          { label: 'Monthly Revenue', value: `$${totalRevenue.toLocaleString()}`, icon: '💰', color: '#059669' },
        ].map((t, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: '12px', padding: '16px', textAlign: 'center', border: `2px solid ${t.color}20`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: '22px', marginBottom: '4px' }}>{t.icon}</div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: t.color }}>{t.value}</div>
            <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t.label}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <input
            type="text"
            placeholder="🔍 Search clients, devices, IMEI..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, maxWidth: '400px', padding: '10px 16px', fontSize: '14px', border: '2px solid #e5e7eb', borderRadius: '10px', outline: 'none', background: '#fff' }}
          />
          <span style={{ fontSize: '13px', color: '#9ca3af' }}>{filteredClients.length} of {clients.length}</span>
        </div>
      </div>

      {filteredClients.length === 0 ? (
        <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af', background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>👤</div>
          <p style={{ margin: 0, fontSize: '15px' }}>{searchTerm ? 'No clients match your search' : 'No clients yet. Add your first client!'}</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '14px' }}>
          {filteredClients.map((client) => {
            const badge = getLoginBadge(client.last_login);
            return (
              <div
                key={client.id}
                onClick={() => onSelectClient(client)}
                style={{
                  cursor: 'pointer', background: '#fff', borderRadius: '12px', padding: '18px',
                  border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  transition: 'box-shadow 0.2s, border-color 0.2s'
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = '#2563eb'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
              >
                {/* Top row: avatar + name + badge */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                  <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: 'linear-gradient(135deg, #2563eb, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '16px', flexShrink: 0 }}>
                    {client.name.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '15px', color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{client.name}</div>
                    <div style={{ fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{client.company_name}</div>
                  </div>
                  <span style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 600, background: badge.bg, color: badge.color, flexShrink: 0 }}>{badge.text}</span>
                </div>

                {/* Stats row */}
                <div style={{ display: 'flex', gap: '0', marginBottom: '12px', background: '#f8fafc', borderRadius: '8px', overflow: 'hidden' }}>
                  {[
                    { label: 'Revenue', value: `$${client.monthly_fee}`, color: '#059669' },
                    { label: 'Devices', value: client.devices?.length || 0, color: '#3b82f6' },
                    { label: 'Users', value: client.user_count || 0, color: '#7c3aed' },
                  ].map((s, i) => (
                    <div key={i} style={{ flex: 1, textAlign: 'center', padding: '10px 4px', borderRight: i < 2 ? '1px solid #e5e7eb' : 'none' }}>
                      <div style={{ fontSize: '16px', fontWeight: 700, color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: '10px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Contact row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '12px' }}>
                  <div style={{ color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>📧 {client.email}</div>
                  <div style={{ color: '#6b7280' }}>📱 {client.phone_cell}</div>
                  {client.phone_work && <div style={{ color: '#6b7280' }}>☎️ {client.phone_work}</div>}
                  <div style={{ color: '#9ca3af' }}>📅 {client.created_at ? new Date(client.created_at).toLocaleDateString() : 'N/A'}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClientList;
